// utils/preferenceMatching.ts
// Logic for matching teacher preferences with curriculum leader nominations

import { TeachingIntention, TeachingPreference } from '../types/intentions';
import { parseClassCode, ClassCodeComponents } from './classCodeParser';
import { findIntelligentMatches, IntelligentMatchResult } from './intelligentPreferenceMatching';
import { PreferencesData } from '../types/intentionPeriods';
import { normalizeLearningArea } from '../constants/learningAreas';

export interface TeacherPreference {
  teacherId: string;
  teacherCode: string;
  teacherName: string;
  preferenceLevel: 'perfect_fit' | 'great_fit' | 'good_fit' | 'avoid';
  addedBy?: string;
  addedAt?: any;
}

export interface CurriculumSubject {
  subjectId: string;
  subjectName: string;
  teacherPreferences: TeacherPreference[];
}

export type PreferenceStrength = 'strong' | 'medium' | 'weak' | 'avoid';

export interface TeacherPreferenceMatch {
  teacherCode: string;
  teacherName: string;
  strength: PreferenceStrength;
  reasons: string[];
  selfNominated: boolean;
  curriculumNominated: boolean;
  curriculumPreferenceLevel?: 'perfect_fit' | 'great_fit' | 'good_fit' | 'avoid';
  /** Did the teacher name this SUBJECT, or only its learning AREA? Drives the
   *  auto-assign priority order — see assignmentPriorityTier. */
  selfNominationKind?: 'subject' | 'area';
  selfPreferenceRank?: number;
  matchScore: number; // 0-100 for sorting
}

/**
 * Enhanced teacher code matching with fuzzy matching and confidence scoring
 */
function findTeacherIntentionWithConfidence(
  teacherCode: string,
  intentions: TeachingIntention[]
): { intention: TeachingIntention | null; confidence: number } {
  if (!teacherCode || intentions.length === 0) {
    return { intention: null, confidence: 0 };
  }

  // Try exact match first (highest confidence)
  let intention = intentions.find(i => i.staffId === teacherCode);
  if (intention) {
    return { intention, confidence: 1.0 };
  }

  // Normalize codes and try again (high confidence)
  const normalizedCode = teacherCode.toUpperCase().trim();
  intention = intentions.find(i => (i.staffId || '').toUpperCase().trim() === normalizedCode);
  if (intention) {
    return { intention, confidence: 0.95 };
  }

  // Try X suffix variations (high confidence - common pattern)
  intention = intentions.find(i => {
    const intentionBase = (i.staffId || '').replace(/X$/, '');
    return intentionBase === teacherCode;
  });
  if (intention) {
    return { intention, confidence: 0.9 };
  }

  const codeWithX = teacherCode + 'X';
  intention = intentions.find(i => i.staffId === codeWithX);
  if (intention) {
    return { intention, confidence: 0.9 };
  }

  // Fuzzy matching for partial codes (medium confidence)
  if (teacherCode.length >= 2) {
    // Try 2-3 character prefix matching with length validation
    intention = intentions.find(i => {
      const staffId = i.staffId || '';
      return staffId.length >= 2 &&
             staffId.substring(0, Math.min(3, teacherCode.length)).toUpperCase() ===
             teacherCode.substring(0, Math.min(3, teacherCode.length)).toUpperCase();
    });
    if (intention) {
      const lengthSimilarity = Math.min(teacherCode.length, intention.staffId?.length || 0) /
                               Math.max(teacherCode.length, intention.staffId?.length || 1);
      return { intention, confidence: 0.6 * lengthSimilarity };
    }
  }

  return { intention: null, confidence: 0 };
}

/**
 * Does `keyword` appear in this text?
 *
 * Short keywords must appear as a WHOLE WORD. The category tables below contain
 * two-letter keys ('pe', 'it', 're'), and a plain substring test matches them
 * inside unrelated words — "s-PE-cialist" made SPECIALIST MATHEMATICS12 register
 * as a PE class, so every teacher who nominated Health & PE was offered as a
 * strong match for Specialist Maths. Longer keywords keep the substring test,
 * which is what lets "mathematics" find SPECIALIST MATHEMATICS12 after the class
 * name has had its spaces and digits stripped.
 *
 * `stripped` is the space/digit-free form, `raw` the original — whole-word tests
 * need the raw text, since stripping spaces destroys word boundaries.
 */
function mentions(stripped: string, raw: string, keyword: string): boolean {
  if (!keyword) return false;
  if (keyword.length <= 3) {
    // Whole-word test, done by tokenising rather than with a regex.
    //
    // The previous attempt built one as new RegExp(`\b${keyword}\b`) — and inside a
    // TEMPLATE LITERAL `\b` is the BACKSPACE escape (U+0008), not a word
    // boundary. The regex was /<BS>pe<BS>/, which matches nothing at all, so every
    // keyword of three characters or fewer ('art', 'pe', 'hpe', 'eng', 'bio', 'it')
    // was silently dead. It suppressed the "s-pe-cialist" false positive by being
    // inert rather than correct (issue #173). Splitting on non-alphanumerics has no
    // escaping subtleties to get wrong, and `raw` is already lowercased.
    return raw.split(/[^a-z0-9]+/).includes(keyword);
  }
  return stripped.includes(keyword);
}

/**
 * Enhanced subject matching with confidence scoring and category hierarchy
 */
function calculateSubjectMatchScore(
  subjectName: string,
  teachingPreference: { specificSubject?: string; learningArea?: string; rank?: number },
  subjectArea?: string
): { score: number; confidence: number; matchType: string } {
  const subject = (subjectName || '').toLowerCase().trim();
  const prefSubject = (teachingPreference.specificSubject || '').toLowerCase().trim();
  const prefArea = (teachingPreference.learningArea || '').toLowerCase().trim();

  // Note: Debug logs removed for production

  // An unnamed class, or a preference carrying neither field, can never match.
  // This guard matters because a derived preference always leaves one field empty
  // (a subject nomination has no learning area and vice versa — see
  // resolveTeachingPreferences in intentionsService), and the equality checks
  // below would otherwise read empty === empty as a perfect match.
  if (!subject || (!prefSubject && !prefArea)) {
    return { score: 0, confidence: 0, matchType: 'none' };
  }

  // Extract core subject name (remove class codes and numbers)
  let coreSubject = subject;
  if (subject.includes(' - ')) {
    coreSubject = subject.split(' - ')[1] || subject;
  }
  coreSubject = coreSubject.replace(/[\d\s]+/g, '').toLowerCase();

  // Stripping digits and spaces empties a purely numeric name; fall back to the
  // full name so it is compared as itself rather than as ''.
  if (!coreSubject) coreSubject = subject;

  // Exact matches (highest confidence). Each side is compared only when the
  // preference actually carries that field.
  if ((prefSubject && prefSubject === coreSubject) || (prefArea && prefArea === coreSubject)) {
    return { score: 50, confidence: 1.0, matchType: 'exact' };
  }

  if ((prefSubject && prefSubject === subject) || (prefArea && prefArea === subject)) {
    return { score: 48, confidence: 0.95, matchType: 'exact-full' };
  }

  // The grid records each class's learning area. When it matches the area the
  // teacher nominated, that is a fact, not an inference — so it beats every
  // keyword-based branch below. Before this, an area nomination could only match
  // by luck of the class NAME: "Arts" never matched DRAMA12, because 'drama' is
  // absent from the category keyword tables, "arts" is too short for the
  // area-substring branch, and an area nomination carries no specificSubject for
  // the substring branch. Both sides are normalised so the school's "HPE" matches
  // the form's "Health & PE" (issue #168).
  if (prefArea && subjectArea) {
    const prefCanonical = normalizeLearningArea(teachingPreference.learningArea);
    const classCanonical = normalizeLearningArea(subjectArea);
    if (prefCanonical && classCanonical && prefCanonical === classCanonical) {
      return { score: 45, confidence: 0.95, matchType: 'area-exact' };
    }
  }

  // Subject category mapping with hierarchy
  const subjectCategories: Record<string, {
    primary: string[];
    secondary: string[];
    related: string[];
  }> = {
    'accounting': {
      primary: ['accounting', 'accountancy'],
      secondary: ['business', 'commerce', 'finance', 'financial'],
      related: ['economics', 'management']
    },
    'mathematics': {
      primary: ['mathematics', 'maths', 'math'],
      secondary: ['calculus', 'algebra', 'statistics', 'stats'],
      related: ['physics', 'science']
    },
    'english': {
      primary: ['english', 'eng'],
      secondary: ['literature', 'writing', 'literacy'],
      related: ['humanities', 'drama', 'media']
    },
    'science': {
      primary: ['science', 'sciences'],
      secondary: ['physics', 'chemistry', 'biology', 'bio', 'chem', 'phys'],
      related: ['mathematics', 'technology']
    },
    'business': {
      primary: ['business', 'commerce'],
      secondary: ['management', 'entrepreneurship', 'marketing'],
      related: ['accounting', 'economics']
    },
    'health': {
      primary: ['health', 'hpe', 'physical education', 'pe'],
      secondary: ['sport', 'fitness', 'wellbeing'],
      related: ['science', 'biology']
    },
    'art': {
      primary: ['art', 'visual art', 'studio art'],
      secondary: ['design', 'creative', 'arts'],
      related: ['media', 'technology']
    },
    'music': {
      primary: ['music', 'musical'],
      secondary: ['band', 'choir', 'instrumental'],
      related: ['performing arts', 'arts']
    },
    'technology': {
      primary: ['technology', 'tech', 'computing', 'digital'],
      secondary: ['computer', 'it', 'engineering', 'stem'],
      related: ['mathematics', 'science']
    }
  };

  // Check category matches
  for (const [category, keywords] of Object.entries(subjectCategories)) {
    // Check if subject matches this category
    const subjectInCategory =
      keywords.primary.some(k => mentions(coreSubject, subject, k) || k.includes(coreSubject)) ||
      keywords.secondary.some(k => mentions(coreSubject, subject, k));

    if (subjectInCategory) {
      const preferenceMentions = (k: string) =>
        mentions(prefSubject, prefSubject, k) || mentions(prefArea, prefArea, k);

      // Check preference matches in this category
      if (keywords.primary.some(preferenceMentions)) {
        return { score: 45, confidence: 0.9, matchType: 'category-primary' };
      }
      if (keywords.secondary.some(preferenceMentions)) {
        return { score: 35, confidence: 0.75, matchType: 'category-secondary' };
      }
      if (keywords.related.some(preferenceMentions)) {
        return { score: 25, confidence: 0.6, matchType: 'category-related' };
      }
    }
  }

  // Substring matching (lower confidence) - stricter rules to prevent false positives
  if (coreSubject.length >= 4 && prefSubject.length >= 4) {
    // Only match if one is significantly contained in the other (not just 2-3 chars)
    const minMatchLength = Math.min(4, Math.min(coreSubject.length, prefSubject.length) * 0.6);

    if (prefSubject.includes(coreSubject) && coreSubject.length >= minMatchLength) {
      return { score: 25, confidence: 0.6, matchType: 'substring' };
    }
    if (coreSubject.includes(prefSubject) && prefSubject.length >= minMatchLength) {
      return { score: 25, confidence: 0.6, matchType: 'substring' };
    }
  }

  // Learning area substring matching - even more restrictive
  if (coreSubject.length >= 5 && prefArea.length >= 5) {
    if (prefArea.includes(coreSubject) || coreSubject.includes(prefArea)) {
      return { score: 20, confidence: 0.5, matchType: 'area-substring' };
    }
  }

  return { score: 0, confidence: 0, matchType: 'none' };
}

/**
 * Calculate Teaching Intentions score (0-50 points)
 */
function calculateTeachingIntentionsScore(
  teacherCode: string,
  subjectName: string,
  intentions: TeachingIntention[],
  subjectArea?: string
): { score: number; details: {
  rank?: number;
  confidence: number;
  matchType: string;
  reason: string;
  /** Whether the winning nomination named this SUBJECT or only its learning AREA.
   *  The auto-assign priority order treats those very differently. */
  nominationKind?: 'subject' | 'area';
}} {
  const { intention, confidence } = findTeacherIntentionWithConfidence(teacherCode, intentions);

  if (!intention) {
    return {
      score: 0,
      details: { confidence: 0, matchType: 'no-intention', reason: 'No teaching intentions found' }
    };
  }

  let bestMatch = {
    score: 0,
    confidence: 0,
    matchType: 'none',
    rank: undefined as number | undefined,
    nominationKind: undefined as 'subject' | 'area' | undefined,
  };

  // Check specific teaching preferences
  if (intention.teachingPreferences) {
    for (const pref of intention.teachingPreferences) {
      const match = calculateSubjectMatchScore(subjectName, pref, subjectArea);
      if (match.score > bestMatch.score) {
        // Score based on preference rank (1st = 50 points, 2nd = 45, etc.)
        const rankMultiplier = pref.rank ? Math.max(0.7, 1.1 - (pref.rank * 0.1)) : 0.8;
        const adjustedScore = Math.round(match.score * rankMultiplier * match.confidence);

        bestMatch = {
          score: adjustedScore,
          confidence: match.confidence * confidence,
          matchType: match.matchType,
          rank: pref.rank,
          // A nomination that names a subject outranks one that names only the
          // learning area, however well the latter happens to score.
          nominationKind: pref.specificSubject ? 'subject' : 'area',
        };
      }
    }
  }

  // Check comfortable teaching areas (lower priority)
  if (bestMatch.score === 0 && intention.comfortableTeachingAreas) {
    for (const area of intention.comfortableTeachingAreas) {
      const match = calculateSubjectMatchScore(subjectName, { learningArea: area }, subjectArea);
      if (match.score > 0) {
        bestMatch = {
          score: Math.round(match.score * 0.4 * match.confidence), // 40% of normal score for comfortable areas
          confidence: match.confidence * confidence * 0.7,
          nominationKind: 'area',
          matchType: 'comfortable-area',
          rank: 10
        };
        break;
      }
    }
  }

  // Separate "we hold nothing for this teacher" from "we hold their preferences
  // and none of them point at this subject". Both used to surface as
  // "No teaching preferences recorded", which reads as a data fault and sent us
  // looking for a loading bug that wasn't there.
  const hasAnyPreference =
    (intention.teachingPreferences?.length ?? 0) > 0 ||
    (intention.comfortableTeachingAreas?.length ?? 0) > 0;
  if (bestMatch.score === 0 && !hasAnyPreference) {
    bestMatch.matchType = 'no-preferences';
  }

  const reason = bestMatch.rank
    ? `${bestMatch.rank}${getOrdinalSuffix(bestMatch.rank)} preference${bestMatch.matchType !== 'exact' ? ` (${getMatchTypeLabel(bestMatch.matchType)})` : ''}`
    : bestMatch.matchType === 'comfortable-area'
      ? 'Listed in comfortable teaching areas'
      : bestMatch.score > 0
        ? getMatchTypeLabel(bestMatch.matchType)
        : 'No relevant preferences found';

  return {
    score: Math.min(50, bestMatch.score),
    details: {
      rank: bestMatch.rank,
      confidence: bestMatch.confidence,
      matchType: bestMatch.matchType,
      nominationKind: bestMatch.nominationKind,
      reason
    }
  };
}

/**
 * Calculate Curriculum Preferences score (0-50 points)
 */
function calculateCurriculumScore(
  teacherCode: string,
  subjectName: string,
  curriculumSubjects: CurriculumSubject[]
): { score: number; details: {
  level?: 'perfect_fit' | 'great_fit' | 'good_fit' | 'avoid';
  confidence: number;
  matchType: string;
  reason: string;
}} {
  if (curriculumSubjects.length === 0) {
    return {
      score: 0,
      details: { confidence: 0, matchType: 'none', reason: 'No curriculum preferences available' }
    };
  }

  // Find matching curriculum subject
  const matchingSubject = curriculumSubjects.find(s =>
    s.subjectName.toLowerCase() === subjectName.toLowerCase() ||
    s.subjectName.toLowerCase().includes(subjectName.toLowerCase()) ||
    subjectName.toLowerCase().includes(s.subjectName.toLowerCase()) ||
    // Try without numbers
    s.subjectName.replace(/\d+/g, '').trim().toLowerCase() ===
    subjectName.replace(/\d+/g, '').trim().toLowerCase()
  );

  if (!matchingSubject) {
    return {
      score: 0,
      details: { confidence: 0, matchType: 'none', reason: 'Subject not found in curriculum preferences' }
    };
  }

  // Find teacher nomination in this subject with enhanced matching
  const nomination = matchingSubject.teacherPreferences?.find(pref => {
    const prefCode = pref.teacherCode;
    const matches = isTeacherCodeMatch(prefCode, teacherCode);

    return matches;
  });

  if (!nomination) {
    return {
      score: 0,
      details: { confidence: 0, matchType: 'none', reason: 'Teacher not nominated by curriculum leader' }
    };
  }

  // Handle avoid preference
  if (nomination.preferenceLevel === 'avoid') {
    return {
      score: -100, // Special case - this will be handled in the main function
      details: {
        level: 'avoid',
        confidence: 1.0,
        matchType: 'avoid',
        reason: 'Curriculum leader marked as avoid'
      }
    };
  }

  // Score based on preference level
  const levelScores = {
    'perfect_fit': 50,
    'great_fit': 35,
    'good_fit': 20,
    'high': 50,        // Add mapping for 'high' level
    'medium': 35,      // Add mapping for 'medium' level
    'low': 20          // Add mapping for 'low' level
  };

  const score = levelScores[nomination.preferenceLevel] || 0;
  const reason = `Curriculum leader preference: ${nomination.preferenceLevel}`;

  return {
    score,
    details: {
      level: nomination.preferenceLevel,
      confidence: 1.0,
      matchType: 'curriculum',
      reason
    }
  };
}

/**
 * Helper function for ordinal suffixes
 */
function getOrdinalSuffix(num: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const mod = num % 100;
  return (mod > 10 && mod < 20) ? 'th' : suffixes[num % 10] || 'th';
}

/**
 * Enhanced teacher code matching to handle legacy and current formats
 */
function isTeacherCodeMatch(curriculumCode: string, staffCode: string): boolean {
  if (!curriculumCode || !staffCode) return false;

  const curriculum = curriculumCode.toUpperCase().trim();
  const staff = staffCode.toUpperCase().trim();

  // Direct matches (highest priority)
  if (curriculum === staff) {
    return true;
  }

  // Handle underscored versions first (JWX_4 <-> JWX4) - more specific than base matching
  if (curriculum.includes('_') || staff.includes('_')) {
    const curriculumClean = curriculum.replace(/[_\-]/g, '');
    const staffClean = staff.replace(/[_\-]/g, '');

    // Exact match after cleaning
    if (curriculumClean === staffClean) {
      return true;
    }

    // Handle JWX_4 <-> JWX4, but be more specific
    if (curriculumClean.endsWith('X')) {
      const baseCode = curriculumClean.slice(0, -1); // JWX4 -> JW4
      // Only match if the staff code is exactly the base code + number
      if (staffClean === baseCode) {
        return true;
      }
    }
  }

  // Handle X suffix variations (ATX <-> AT2, ATX <-> AT)
  if (curriculum.endsWith('X')) {
    const curriculumBase = curriculum.slice(0, -1); // ATX -> AT

    // Match ATX with AT2, AT3, etc. - but only if the pattern is clear
    if (staff.startsWith(curriculumBase) && staff.length === curriculumBase.length + 1 && /\d$/.test(staff)) {
      return true;
    }

    // Match ATX with AT exactly
    if (staff === curriculumBase) {
      return true;
    }
  }

  // Handle staff with X suffix matching curriculum without (AT2X <-> AT2)
  if (staff.endsWith('X') && staff.slice(0, -1) === curriculum) {
    return true;
  }

  // REMOVED: Overly broad 2-letter base matching that was causing false positives
  // This was matching JWX_4 with any JW* code, which is too permissive

  return false;
}

/**
 * Helper function to get user-friendly match type labels
 */
function getMatchTypeLabel(matchType: string): string {
  switch (matchType) {
    case 'exact':
      return 'Exact subject match';
    case 'exact-full':
      return 'Exact match';
    case 'category-primary':
      return 'Strong subject area match';
    case 'category-secondary':
      return 'Related subject match';
    case 'category-related':
      return 'Subject area connection';
    case 'substring':
      return 'Similar subject name';
    case 'area-exact':
      return 'Learning area match';
    case 'area-substring':
      return 'Related learning area';
    case 'comfortable-area':
      return 'Comfortable teaching area';
    case 'none':
      return 'No specific match found';
    default:
      return 'Subject match found';
  }
}

/**
 * Map legacy preference levels to new standardized format
 */
export function mapPreferenceLevel(level: string): 'perfect_fit' | 'great_fit' | 'good_fit' | 'avoid' {
  const levelMap: Record<string, 'perfect_fit' | 'great_fit' | 'good_fit' | 'avoid'> = {
    'high': 'perfect_fit',
    'medium': 'great_fit',
    'low': 'good_fit',
    'avoid': 'avoid',
    'perfect_fit': 'perfect_fit',
    'great_fit': 'great_fit',
    'good_fit': 'good_fit'
  };

  return levelMap[level.toLowerCase()] || 'good_fit';
}

/**
 * Calculate preference strength for a teacher teaching a specific subject
 * Uses balanced 50/50 weighting between Teaching Intentions and Curriculum preferences
 */
export function calculatePreferenceStrength(
  teacherCode: string,
  subjectName: string,
  intentions: TeachingIntention[],
  curriculumSubjects: CurriculumSubject[],
  /** The class's recorded learning area (`ClassAllocation.curriculumArea`). Pass it
   *  wherever it is known — without it, area nominations can only match by luck of
   *  the class name. Optional so existing callers keep compiling. */
  subjectArea?: string
): TeacherPreferenceMatch {
  // Calculate Teaching Intentions score (0-50)
  const intentionsResult = calculateTeachingIntentionsScore(teacherCode, subjectName, intentions, subjectArea);

  // Calculate Curriculum Preferences score (0-50)
  const curriculumResult = calculateCurriculumScore(teacherCode, subjectName, curriculumSubjects);

  // Handle avoid preference immediately
  if (curriculumResult.score === -100) {
    const teacherName = intentions.find(i => {
      const { intention } = findTeacherIntentionWithConfidence(teacherCode, intentions);
      return intention === i;
    })?.staffName ||
    curriculumSubjects.find(s => s.teacherPreferences?.some(p => p.teacherCode === teacherCode))
      ?.teacherPreferences?.find(p => p.teacherCode === teacherCode)?.teacherName ||
    teacherCode;

    return {
      teacherCode,
      teacherName,
      strength: 'avoid',
      reasons: [curriculumResult.details.reason],
      selfNominated: intentionsResult.score > 0,
      curriculumNominated: true,
      curriculumPreferenceLevel: 'avoid',
      selfPreferenceRank: intentionsResult.details.rank,
      matchScore: 0
    };
  }

  // Calculate total score (0-100)
  const totalScore = intentionsResult.score + curriculumResult.score;

  // Determine strength based on new thresholds
  let strength: PreferenceStrength;
  if (totalScore >= 70) {
    strength = 'strong';
  } else if (totalScore >= 40) {
    strength = 'medium';
  } else if (totalScore >= 25) {
    strength = 'weak';
  } else {
    strength = 'weak';
  }

  // Build reasons array with more honest assessment
  const reasons: string[] = [];

  // Only include intention reasons if the score is meaningful (25+ points)
  if (intentionsResult.score >= 25) {
    reasons.push(intentionsResult.details.reason);
  }

  // Always include curriculum reasons if present
  if (curriculumResult.score > 0) {
    reasons.push(curriculumResult.details.reason);
  }

  // Provide honest feedback for weak or no matches
  if (reasons.length === 0) {
    if (intentionsResult.score > 0 && intentionsResult.score < 25) {
      // Teacher has preferences but they don't match this subject well
      reasons.push('No preference for this subject area');
    } else if (intentionsResult.score === 0) {
      // Say which of the three it actually is, rather than blaming the data.
      reasons.push(
        intentionsResult.details.matchType === 'no-intention'
          ? 'No teaching intentions submitted'
          : intentionsResult.details.matchType === 'no-preferences'
            ? 'No teaching preferences recorded'
            : 'No preference for this subject'
      );
    } else {
      reasons.push('No specific preference indicated');
    }
  }

  // Get teacher name
  const teacherIntention = findTeacherIntentionWithConfidence(teacherCode, intentions).intention;
  const curriculumNomination = curriculumSubjects
    .flatMap(s => s.teacherPreferences || [])
    .find(p => p.teacherCode === teacherCode);

  const teacherName = teacherIntention?.staffName ||
                     curriculumNomination?.teacherName ||
                     teacherCode;

  return {
    teacherCode,
    teacherName,
    strength,
    reasons,
    selfNominated: intentionsResult.score > 0,
    curriculumNominated: curriculumResult.score > 0,
    curriculumPreferenceLevel: curriculumResult.details.level,
    selfPreferenceRank: intentionsResult.details.rank,
    selfNominationKind: intentionsResult.details.nominationKind,
    matchScore: totalScore
  };
}

/**
 * Get all teacher preference matches for a subject, sorted by strength
 */
export function getTeacherPreferencesForSubject(
  subjectName: string,
  allTeachers: { initial: string; name: string }[],
  intentions: TeachingIntention[],
  curriculumSubjects: CurriculumSubject[],
  subjectArea?: string
): TeacherPreferenceMatch[] {
  return allTeachers
    .map(teacher => calculatePreferenceStrength(
      teacher.initial,
      subjectName,
      intentions,
      curriculumSubjects,
      subjectArea
    ))
    .filter(match => match.strength !== 'avoid') // Don't show avoided teachers in main list
    .sort((a, b) => b.matchScore - a.matchScore); // Sort by match score (descending)
}

/**
 * Find personal preference matches for auto-assignment
 * A personal preference match requires:
 * 1. Teacher self-nominated for the subject
 * 2. Teacher has sufficient available minutes
 * 3. Deduplicated - only one match per teacher per subject (best available slot)
 */
/**
 * Normalise a class name for "did they teach this last year?" comparisons.
 *
 * Subject names carry a unit/level suffix that changes between years — a teacher
 * who had BIOLOGY12 in 2026 may be up for BIOLOGY34 in 2027. Stripping digits and
 * punctuation compares the SUBJECT rather than the exact class, which is what
 * "taught it last year" means to a timetabler.
 */
function normaliseSubjectKey(name: string | undefined | null): string {
  return String(name || '').toLowerCase().replace(/[^a-z]/g, '');
}

/**
 * Index of who taught what in a previous year, built from that year's grids.
 *
 * The intended source, `pit.staff_teaching_history`, is empty — the Synergetic
 * import that fills it has never run. The prior year's grids are the real record:
 * 1,633 assignments across the 2026 grids as at 2026-08-27.
 *
 * Returns a Set of `teacherCode::subjectKey`. Pass it to the finders as
 * `priorTeaching`; omit it and the priority order simply never awards the
 * "taught it last year" tiers, rather than misreporting them.
 */
export function buildPriorTeachingIndex(grids: any[], priorYear: number): Set<string> {
  const index = new Set<string>();
  for (const grid of grids || []) {
    if (!grid || grid.year !== priorYear) continue;
    for (const cls of grid.classes || []) {
      const key = normaliseSubjectKey(cls?.className);
      if (!key) continue;
      for (const assigned of Object.values(cls.lineAssignments || {})) {
        if (typeof assigned !== 'string' || !assigned) continue;
        if (assigned.startsWith('[') || assigned.startsWith('{')) {
          try {
            const parsed = JSON.parse(assigned);
            if (Array.isArray(parsed)) {
              parsed.forEach((a: any) => a?.teacherCode && index.add(`${a.teacherCode}::${key}`));
            }
          } catch {
            index.add(`${assigned}::${key}`);
          }
        } else {
          index.add(`${assigned}::${key}`);
        }
      }
    }
  }
  return index;
}

function taughtLastYear(
  priorTeaching: Set<string> | undefined,
  teacherCode: string,
  className: string
): boolean {
  if (!priorTeaching || priorTeaching.size === 0) return false;
  return priorTeaching.has(`${teacherCode}::${normaliseSubjectKey(className)}`);
}

/**
 * Where an auto-assign proposal sits in the school's priority order.
 *
 * 1. Teacher named the subject AND their leader nominated them AND they taught it
 *    last year
 * 2. Teacher named the subject AND their leader nominated them
 * 3. Teacher named the subject
 * 4. Leader nominated the teacher for the subject
 * 5. Teacher named only the learning area, and taught it last year
 * 6. Teacher named only the learning area
 *
 * A teacher who named only the AREA but whose leader also nominated them satisfies
 * tier 4, not tiers 1–2: those require the teacher to have named the subject
 * itself. Within a tier, ties break on match score then nomination rank, so those
 * still sort above a bare leader nomination.
 *
 * 99 means "outside the order" and sorts last; nothing should normally land there.
 */
export function assignmentPriorityTier(
  match: { selfNominated?: boolean; selfNominationKind?: 'subject' | 'area'; curriculumNominated?: boolean },
  taught: boolean
): number {
  const namedSubject = !!match.selfNominated && match.selfNominationKind === 'subject';
  const namedArea = !!match.selfNominated && match.selfNominationKind === 'area';
  const leader = !!match.curriculumNominated;

  if (namedSubject && leader) return taught ? 1 : 2;
  if (namedSubject) return 3;
  if (leader) return 4;
  if (namedArea) return taught ? 5 : 6;
  return 99;
}

/**
 * Comparator for proposals: priority tier first, then match score, then the
 * teacher's own ranking. Every place that picks a "best" match must use this, or
 * a tier-3 proposal with a high score would beat a tier-1 one.
 */
function byPriority(a: any, b: any): number {
  return (
    (a.priorityTier ?? 99) - (b.priorityTier ?? 99) ||
    (b.matchScore || 0) - (a.matchScore || 0) ||
    (a.preferenceRank ?? 999) - (b.preferenceRank ?? 999)
  );
}

/** Order proposals by the school's priority tiers, best first. */
function sortByPriority(matches: any[]): any[] {
  return [...matches].sort(byPriority);
}

/**
 * The lines a class may actually be scheduled on.
 *
 * `allowedLineIds` is the grid's own record of where a subject can run, and the
 * grid editor honours it. Auto-assign did not: it enumerated every line A–L, and
 * because the line plays no part in the score, every variant of a (teacher,
 * subject) match tied and the stable sort kept the first — so 100% of proposals
 * landed on line A and 57.7% of them named a line the class is barred from
 * (issue #173).
 *
 * A class whose allowed lines are all absent from the grid yields no candidates.
 * Proposing it somewhere arbitrary would be worse than proposing it nowhere.
 */
function candidateLinesFor(classItem: any, lines?: any[]): string[] {
  const gridLineIds =
    Array.isArray(lines) && lines.length > 0
      ? lines.map(l => (typeof l === 'string' ? l : l?.id)).filter(Boolean)
      : ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];

  const allowed = classItem?.allowedLineIds;
  if (Array.isArray(allowed) && allowed.length > 0) {
    return gridLineIds.filter(id => allowed.includes(id));
  }
  return gridLineIds;
}

/**
 * Is this teacher already teaching something else on this line?
 *
 * One person cannot be in two rooms at once, so this is a hard clash — the worst
 * outcome auto-assign can produce. The finders only ever checked whether THIS
 * class's line was taken, never whether the TEACHER was already on it, so a
 * second run, or a run over a partly-staffed grid, would double-book (issue #173,
 * reproduced by execution).
 *
 * Handles the multi-teacher JSON form that `lineAssignments` can hold.
 */
function teacherAlreadyOnLine(
  classes: any[],
  lineId: string,
  teacherCode: string,
  exceptClassId?: string
): boolean {
  if (!teacherCode) return false;
  return (classes || []).some(c => {
    if (!c || c.id === exceptClassId) return false;
    const assigned = c.lineAssignments?.[lineId];
    if (!assigned) return false;
    if (typeof assigned === 'string' && (assigned.startsWith('[') || assigned.startsWith('{'))) {
      try {
        const parsed = JSON.parse(assigned);
        return Array.isArray(parsed) && parsed.some((a: any) => a?.teacherCode === teacherCode);
      } catch {
        return assigned === teacherCode;
      }
    }
    return assigned === teacherCode;
  });
}

/**
 * Staff who can actually be given a class: active, and carrying a code the grid
 * can match. Auto-assign iterated every staff row regardless of status.
 */
/**
 * Which semester a line belongs to.
 *
 * Line objects carry `studyPeriod` (see transformGrid.ts); `semester` is not a
 * field on them, so `lineData?.semester` was always undefined and every line —
 * including G–L — was scored against semester 1.
 */
function semesterForLine(lineId: string, lines?: any[]): string {
  const lineData = Array.isArray(lines) ? lines.find(l => l?.id === lineId) : undefined;
  return (
    lineData?.studyPeriod ||
    lineData?.semester ||
    (['A', 'B', 'C', 'D', 'E', 'F'].includes(lineId) ? 'semester1' : 'semester2')
  );
}

/**
 * Drop proposals that would push a teacher past their available minutes.
 *
 * The per-match capacity test upstream reads the teacher's load from the SAVED
 * grid, which does not change while a run is being assembled — so a teacher could
 * collect any number of proposals in one run and each would independently look
 * affordable. This pass walks the finished list best-score-first, keeping a
 * running total per (teacher, semester), and drops what no longer fits.
 */
function enforceTeacherCapacity(
  matches: any[],
  getTeacherLoad: (teacherCode: string, semester: string) => { availableMinutes: number; allocatedMinutes: number },
  lines?: any[]
): any[] {
  if (!getTeacherLoad) return matches;
  const running = new Map<string, number>();
  const kept: any[] = [];

  for (const match of sortByPriority(matches)) {
    const semester = semesterForLine(match.lineId, lines);
    const key = `${match.teacherCode}::${semester}`;
    let load;
    try {
      load = getTeacherLoad(match.teacherCode, semester);
    } catch {
      continue;
    }
    const used = running.get(key) ?? load.allocatedMinutes ?? 0;
    const required = match.requiredMinutes || 440;
    if (used + required > load.availableMinutes) continue;
    running.set(key, used + required);
    kept.push(match);
  }
  return kept;
}

function assignableStaff(staff: any[]): any[] {
  return (staff || []).filter(s => s && s.initial && (s.status ?? 'active') === 'active');
}

export function findPersonalPreferenceMatches(
  classes: any[], // Grid classes
  staff: any[], // All staff
  intentions: TeachingIntention[],
  getTeacherLoad: (teacherCode: string, semester: string) => { availableMinutes: number; allocatedMinutes: number },
  lines?: any[], // Grid lines with actual line IDs
  /** From buildPriorTeachingIndex — enables the "taught it last year" tiers. */
  priorTeaching?: Set<string>,
  /** Not an inclusion criterion here: used only to rank a self-nomination that a
   *  leader also backs above one they didn't, and to honour an 'avoid' flag. */
  curriculumSubjects: CurriculumSubject[] = []
): Array<{
  classId: string;
  className: string;
  lineId: string;
  teacherCode: string;
  teacherName: string;
  subjectName: string;
  reasons: string[];
  availableMinutes: number;
  requiredMinutes: number;
  matchType: 'personal';
}> {
  const allPotentialMatches: Array<any> = [];

  // A draft is a form the teacher has not submitted. It must not staff a class.
  const submittedIntentions = (intentions || []).filter(i => i?.status !== 'draft');

  classes.forEach(classItem => {
    const possibleLines = candidateLinesFor(classItem, lines);

    possibleLines.forEach(lineId => {
      if (classItem.lineAssignments && classItem.lineAssignments[lineId]) {
        return;
      }

      const subjectName = classItem.subject || classItem.className;
      
      assignableStaff(staff).forEach(teacher => {
        const match = calculatePreferenceStrength(
          teacher.initial,
          subjectName,
          submittedIntentions,
          curriculumSubjects,
          classItem.curriculumArea
        );

        // A leader's explicit "avoid" outranks the teacher's own interest.
        if (match.strength === 'avoid') return;

        if (match.selfNominated) {
          // A teacher already on this line cannot take another class on it.
          if (teacherAlreadyOnLine(classes, lineId, teacher.initial, classItem.id)) {
            return;
          }

          const semester = semesterForLine(lineId, lines);
          const load = getTeacherLoad(teacher.initial, semester);

          // Use the class's own minutes; 440 is only a fallback.
          const requiredMinutes = classItem.minutesPerWeek || 440;

          if (load.availableMinutes - load.allocatedMinutes >= requiredMinutes) {
            // v1.4.0: stamp the teacher's isLate flag onto the match for tiebreak sorting
            const teacherIntention = submittedIntentions.find(i => i.staffId === teacher.initial);
            allPotentialMatches.push({
              classId: classItem.id,
              className: classItem.className,
              lineId,
              teacherCode: teacher.initial,
              teacherName: teacher.name,
              subjectName,
              reasons: match.reasons,
              availableMinutes: load.availableMinutes,
              requiredMinutes,
              matchType: 'personal',
              matchScore: match.matchScore,
              priorityTier: assignmentPriorityTier(
                match,
                taughtLastYear(priorTeaching, teacher.initial, classItem.className)
              ),
              taughtLastYear: taughtLastYear(priorTeaching, teacher.initial, classItem.className),
              preferenceRank: match.selfPreferenceRank || 999,
              isLate: teacherIntention?.isLate === true
            });
          }
        }
      });
    });
  });

  // Deduplicate: Keep only the best match for each teacher-subject combination
  const deduplicatedMatches = deduplicateMatches(allPotentialMatches);
  return sortByPriority(enforceTeacherCapacity(deduplicatedMatches, getTeacherLoad, lines));
}

/**
 * Every rule at once, as one ranked list.
 *
 * The three modes are NESTED, not alternatives: "perfect" is a strict subset of
 * both of the others. Before the priority order existed they WERE the ranking,
 * chosen by hand; now the tiers do that job, and picking one mode just hides
 * whole categories of valid match. Running Personal alone, for instance, misses
 * every teacher a leader nominated who did not also ask for the subject.
 *
 * Merging cannot be a plain concatenation. Each finder guarantees internally that
 * it proposes one teacher per (class, line) and never double-books a teacher on a
 * line — but two independently-guarded lists laid end to end can violate both. So
 * the merged set is re-run through the same guards:
 *
 *   1. one proposal per (class, line, teacher) — keep the best tier
 *   2. one proposal per (class, line)          — two teachers cannot share a slot
 *   3. removeLineLevelDuplicates                — a teacher cannot be twice on a line
 *   4. enforceTeacherCapacity                   — cumulative minutes still hold
 */
export function findAllMatches(
  classes: any[],
  staff: any[],
  intentions: TeachingIntention[],
  curriculumSubjects: CurriculumSubject[],
  getTeacherLoad: (teacherCode: string, semester: string) => { availableMinutes: number; allocatedMinutes: number },
  lines?: any[],
  priorTeaching?: Set<string>
): any[] {
  const merged = [
    ...findPerfectMatches(classes, staff, intentions, curriculumSubjects, getTeacherLoad, lines, priorTeaching),
    ...findPersonalPreferenceMatches(classes, staff, intentions, getTeacherLoad, lines, priorTeaching, curriculumSubjects),
    ...findCurriculumPreferenceMatches(classes, staff, curriculumSubjects, getTeacherLoad, lines, priorTeaching, intentions),
  ];

  // 1 + 2: best proposal per slot, best tier wins.
  const bestPerSlot = new Map<string, any>();
  for (const match of sortByPriority(merged)) {
    const slot = `${match.classId}::${match.lineId}`;
    if (!bestPerSlot.has(slot)) bestPerSlot.set(slot, match);
  }

  // 3 + 4: the cross-cutting guards, applied to the merged set as a whole.
  const deduped = removeLineLevelDuplicates([...bestPerSlot.values()]);
  return sortByPriority(enforceTeacherCapacity(deduped, getTeacherLoad, lines));
}

/**
 * Find curriculum preference matches for auto-assignment
 * A curriculum preference match requires:
 * 1. Curriculum leader nominated the teacher for the subject
 * 2. Teacher has sufficient available minutes
 * 3. Deduplicated - only one match per teacher per subject (best available slot)
 */
export function findCurriculumPreferenceMatches(
  classes: any[], // Grid classes
  staff: any[], // All staff
  curriculumSubjects: CurriculumSubject[],
  getTeacherLoad: (teacherCode: string, semester: string) => { availableMinutes: number; allocatedMinutes: number },
  lines?: any[], // Grid lines with actual line IDs
  /** From buildPriorTeachingIndex — enables the "taught it last year" tiers. */
  priorTeaching?: Set<string>,
  /** Not an inclusion criterion here: used only to rank a leader nomination the
   *  teacher also asked for above one they didn't. */
  intentions: TeachingIntention[] = []
): Array<{
  classId: string;
  className: string;
  lineId: string;
  teacherCode: string;
  teacherName: string;
  subjectName: string;
  reasons: string[];
  availableMinutes: number;
  requiredMinutes: number;
  matchType: 'curriculum';
}> {
  if (!classes || classes.length === 0) {
    console.warn('⚠️ findCurriculumPreferenceMatches: No classes provided');
    return [];
  }

  if (!staff || staff.length === 0) {
    console.warn('⚠️ findCurriculumPreferenceMatches: No staff provided');
    return [];
  }

  if (!curriculumSubjects || curriculumSubjects.length === 0) {
    console.warn('⚠️ findCurriculumPreferenceMatches: No curriculum subjects provided');
    return [];
  }

  const allPotentialMatches: Array<any> = [];

  classes.forEach((classItem, classIndex) => {
    const possibleLines = candidateLinesFor(classItem, lines);

    const subjectName = classItem.subject || classItem.className;

    possibleLines.forEach(lineId => {
      if (classItem.lineAssignments && classItem.lineAssignments[lineId]) {
        return; // Skip assigned lines
      }

      let lineMatches = 0;

      assignableStaff(staff).forEach(teacher => {
        const match = calculatePreferenceStrength(
          teacher.initial,
          subjectName,
          [],
          curriculumSubjects,
          classItem.curriculumArea
        );

        if (match.curriculumNominated && match.curriculumPreferenceLevel !== 'avoid') {
          if (teacherAlreadyOnLine(classes, lineId, teacher.initial, classItem.id)) {
            return;
          }

          const semester = semesterForLine(lineId, lines);

          // Load check. The old fallback of 1000 free minutes silently waved every
          // teacher through when the caller passed no load function.
          let load: { availableMinutes: number; allocatedMinutes: number } | null = null;
          if (getTeacherLoad) {
            try {
              load = getTeacherLoad(teacher.initial, semester);
            } catch (error) {
              load = null;
            }
          }
          if (!load) return;

          const requiredMinutes = classItem.minutesPerWeek || 440;

          if (load.availableMinutes - load.allocatedMinutes >= requiredMinutes) {
            allPotentialMatches.push({
              classId: classItem.id,
              className: classItem.className,
              lineId,
              teacherCode: teacher.initial,
              teacherName: teacher.name,
              subjectName,
              reasons: match.reasons,
              availableMinutes: load.availableMinutes,
              requiredMinutes,
              matchType: 'curriculum',
              matchScore: match.matchScore,
              priorityTier: assignmentPriorityTier(
                match,
                taughtLastYear(priorTeaching, teacher.initial, classItem.className)
              ),
              taughtLastYear: taughtLastYear(priorTeaching, teacher.initial, classItem.className),
              curriculumPreferenceLevel: match.curriculumPreferenceLevel
            });
            lineMatches++;
          }
        }
      });
    });
  });

  // Deduplicate: Keep only the best match for each teacher-subject combination
  const deduplicatedMatches = deduplicateMatches(allPotentialMatches);

  return sortByPriority(enforceTeacherCapacity(deduplicatedMatches, getTeacherLoad, lines));
}

/**
 * Deduplicates matches to ensure only one match per teacher per subject
 * Prioritizes matches with higher scores and better curriculum preference levels
 */
function deduplicateMatches(matches: any[]): any[] {
  const teacherSubjectMap = new Map<string, any[]>();
  
  // Group matches by teacher-subject combination
  matches.forEach(match => {
    const key = `${match.teacherCode}-${match.subjectName}`;
    if (!teacherSubjectMap.has(key)) {
      teacherSubjectMap.set(key, []);
    }
    teacherSubjectMap.get(key)!.push(match);
  });

  const deduplicatedMatches: any[] = [];

  // For each teacher-subject combination, pick the best match
  teacherSubjectMap.forEach((matchesForTeacherSubject) => {
    if (matchesForTeacherSubject.length === 1) {
      deduplicatedMatches.push(matchesForTeacherSubject[0]);
    } else {
      // Sort matches by priority criteria
      const sortedMatches = matchesForTeacherSubject.sort((a, b) => {
        // 0. Priority tier first — a tier-1 proposal must never lose to a
        //    higher-scoring tier-3 one.
        if ((a.priorityTier ?? 99) !== (b.priorityTier ?? 99)) {
          return (a.priorityTier ?? 99) - (b.priorityTier ?? 99);
        }

        // 1. Higher match score is better
        if (a.matchScore !== b.matchScore) {
          return b.matchScore - a.matchScore;
        }
        
        // 2. For curriculum matches, 'high' preference is better than 'medium' 
        if (a.curriculumPreferenceLevel && b.curriculumPreferenceLevel) {
          const levelPriority = { 'high': 3, 'medium': 2, 'low': 1 };
          const aPriority = levelPriority[a.curriculumPreferenceLevel] || 0;
          const bPriority = levelPriority[b.curriculumPreferenceLevel] || 0;
          if (aPriority !== bPriority) {
            return bPriority - aPriority;
          }
        }

        // 3. For personal matches, lower preference rank is better (1st preference better than 2nd)
        if (a.preferenceRank && b.preferenceRank) {
          return a.preferenceRank - b.preferenceRank;
        }

        // 4. More available minutes is better
        return b.availableMinutes - a.availableMinutes;
      });

      deduplicatedMatches.push(sortedMatches[0]);
    }
  });

  // SMART DISTRIBUTION: Handle multiple teachers for the same subject intelligently
  return distributeTeachersAcrossClasses(deduplicatedMatches);
}

/**
 * Smart distribution logic to handle multiple teachers wanting the same subject
 * - If there are multiple classes of the same subject, spread teachers across them
 * - If there are more teachers than classes, mark excess as requiring user choice
 * - Prevents duplicate teacher assignments per line
 * - Uses preference scarcity analysis for optimal distribution
 */
function distributeTeachersAcrossClasses(matches: any[]): any[] {
  // STEP 1: Remove line-level duplicates first
  const lineDeduplicatedMatches = removeLineLevelDuplicates(matches);
  
  // STEP 2: Apply preference scarcity analysis
  const smartDistributedMatches = applyPreferenceScarcityLogic(lineDeduplicatedMatches);
  
  return smartDistributedMatches;
}

/**
 * Remove duplicate teacher assignments per line
 * If a teacher appears multiple times in the same line, keep only their best match
 */
function removeLineLevelDuplicates(matches: any[]): any[] {
  const lineTeacherMap = new Map<string, any[]>();
  
  // Group matches by line-teacher combination
  matches.forEach(match => {
    const key = `${match.lineId}-${match.teacherCode}`;
    if (!lineTeacherMap.has(key)) {
      lineTeacherMap.set(key, []);
    }
    lineTeacherMap.get(key)!.push(match);
  });

  const deduplicatedMatches: any[] = [];

  // For each line-teacher combination, pick the best match
  lineTeacherMap.forEach((lineTeacherMatches, key) => {
    if (lineTeacherMatches.length === 1) {
      deduplicatedMatches.push(lineTeacherMatches[0]);
    } else {
      // Sort by match score and preference rank
      const bestMatch = lineTeacherMatches.sort((a, b) => {
        // Priority tier first, for the same reason as above.
        if ((a.priorityTier ?? 99) !== (b.priorityTier ?? 99)) {
          return (a.priorityTier ?? 99) - (b.priorityTier ?? 99);
        }
        // Higher match score is better
        if (a.matchScore !== b.matchScore) {
          return b.matchScore - a.matchScore;
        }
        // Lower preference rank is better (1st preference > 2nd preference)
        if (a.preferenceRank && b.preferenceRank) {
          return a.preferenceRank - b.preferenceRank;
        }
        return 0;
      })[0];
      
      deduplicatedMatches.push(bestMatch);
    }
  });

  return deduplicatedMatches;
}

/**
 * Build a map of teachers and their available preference options
 * Used to calculate scarcity scores for smart distribution
 */
function buildTeacherPreferenceMap(matches: any[]): Map<string, any[]> {
  const teacherPreferences = new Map<string, any[]>();
  
  matches.forEach(match => {
    if (!teacherPreferences.has(match.teacherCode)) {
      teacherPreferences.set(match.teacherCode, []);
    }
    teacherPreferences.get(match.teacherCode)!.push(match);
  });


  return teacherPreferences;
}

/**
 * Apply preference scarcity logic for optimal assignment distribution
 * Teachers with fewer high-preference options get priority for contested subjects
 */
function applyPreferenceScarcityLogic(matches: any[]): any[] {
  // Build preference scarcity scores for each teacher
  const teacherPreferenceOptions = buildTeacherPreferenceMap(matches);
  
  // Group matches by BASE subject name (removing S1/S2 suffix) to find conflicts  
  const subjectGroups = new Map<string, any[]>();
  
  matches.forEach(match => {
    // Extract base subject (e.g., "ACCOUNTING12 S1" -> "ACCOUNTING12")
    const baseSubject = match.subjectName.replace(/\s*(S1|S2)$/, '');
    if (!subjectGroups.has(baseSubject)) {
      subjectGroups.set(baseSubject, []);
    }
    subjectGroups.get(baseSubject)!.push(match);
  });

  const finalMatches: any[] = [];

  subjectGroups.forEach((subjectMatches, baseSubject) => {
    if (subjectMatches.length <= 1) {
      // No conflict - single teacher wants this subject
      finalMatches.push(...subjectMatches);
      return;
    }

    // Multiple teachers want this subject - need smart distribution
    const uniqueClasses = new Set(subjectMatches.map(m => m.classId));
    const availableClasses = Array.from(uniqueClasses);
    
    // Get unique teachers wanting this subject
    const teacherMatches = new Map<string, any[]>();
    subjectMatches.forEach(match => {
      if (!teacherMatches.has(match.teacherCode)) {
        teacherMatches.set(match.teacherCode, []);
      }
      teacherMatches.get(match.teacherCode)!.push(match);
    });

    const teachers = Array.from(teacherMatches.keys());

    if (teachers.length <= availableClasses.length) {
      // Enough classes for all teachers - use scarcity-based distribution
      const scarcitySortedTeachers = teachers.sort((teacherA, teacherB) => {
        // Calculate scarcity scores - teachers with fewer options get priority
        const optionsA = teacherPreferenceOptions.get(teacherA)?.length || 0;
        const optionsB = teacherPreferenceOptions.get(teacherB)?.length || 0;

        // Calculate high-preference count
        const highPrefA = teacherPreferenceOptions.get(teacherA)?.filter(p => p.matchScore >= 80).length || 0;
        const highPrefB = teacherPreferenceOptions.get(teacherB)?.filter(p => p.matchScore >= 80).length || 0;


        // Primary: Teacher with fewer high-preference options gets priority
        if (highPrefA !== highPrefB) {
          return highPrefA - highPrefB; // Lower = higher priority
        }

        // Secondary: Teacher with fewer total options gets priority
        if (optionsA !== optionsB) {
          return optionsA - optionsB; // Lower = higher priority
        }

        // Tertiary: Higher match score for this specific subject
        const bestMatchA = teacherMatches.get(teacherA)!.sort((a, b) => b.matchScore - a.matchScore)[0];
        const bestMatchB = teacherMatches.get(teacherB)!.sort((a, b) => b.matchScore - a.matchScore)[0];
        if (bestMatchA.matchScore !== bestMatchB.matchScore) {
          return bestMatchB.matchScore - bestMatchA.matchScore;
        }

        // Quaternary (v1.4.0): on-time submitters win over late submitters
        const lateA = bestMatchA.isLate === true;
        const lateB = bestMatchB.isLate === true;
        if (lateA !== lateB) return lateA ? 1 : -1; // late loses => goes later
        return 0;
      });

      
      scarcitySortedTeachers.forEach((teacherCode, index) => {
        const teacherOptions = teacherMatches.get(teacherCode)!;
        
        // Find the best match for this teacher in the assigned class
        const assignedClassId = availableClasses[index];
        const bestMatch = teacherOptions
          .filter(m => m.classId === assignedClassId)
          .sort((a, b) => b.matchScore - a.matchScore)[0];

        if (bestMatch) {
          finalMatches.push(bestMatch);
        }
      });
    } else {
      // More teachers than classes - use scarcity logic to auto-assign, but mark as contested
      
      // Sort teachers by scarcity (same logic as above)
      const scarcitySortedTeachers = teachers.sort((teacherA, teacherB) => {
        const optionsA = teacherPreferenceOptions.get(teacherA)?.length || 0;
        const optionsB = teacherPreferenceOptions.get(teacherB)?.length || 0;
        const highPrefA = teacherPreferenceOptions.get(teacherA)?.filter(p => p.matchScore >= 80).length || 0;
        const highPrefB = teacherPreferenceOptions.get(teacherB)?.filter(p => p.matchScore >= 80).length || 0;
        
        // Primary: Teacher with fewer high-preference options gets priority
        if (highPrefA !== highPrefB) return highPrefA - highPrefB;
        // Secondary: Teacher with fewer total options gets priority
        if (optionsA !== optionsB) return optionsA - optionsB;
        // Tertiary: Higher match score for this specific subject
        const bestMatchA = teacherMatches.get(teacherA)!.sort((a, b) => b.matchScore - a.matchScore)[0];
        const bestMatchB = teacherMatches.get(teacherB)!.sort((a, b) => b.matchScore - a.matchScore)[0];
        if (bestMatchA.matchScore !== bestMatchB.matchScore) {
          return bestMatchB.matchScore - bestMatchA.matchScore;
        }
        // Quaternary (v1.4.0): on-time submitters win over late submitters
        const lateA = bestMatchA.isLate === true;
        const lateB = bestMatchB.isLate === true;
        if (lateA !== lateB) return lateA ? 1 : -1;
        return 0;
      });


      // Assign classes to teachers in scarcity order (highest priority first)
      for (let i = 0; i < Math.min(scarcitySortedTeachers.length, availableClasses.length); i++) {
        const teacherCode = scarcitySortedTeachers[i];
        const teacherOptions = teacherMatches.get(teacherCode)!;
        const assignedClassId = availableClasses[i];
        
        const bestMatch = teacherOptions
          .filter(m => m.classId === assignedClassId)
          .sort((a, b) => b.matchScore - a.matchScore)[0];
          
        if (bestMatch) {
          // Mark as contested but assign it
          bestMatch.isContested = true;
          bestMatch.contestedInfo = {
            totalCompetitors: teachers.length,
            assignedByScarcity: true
          };
          finalMatches.push(bestMatch);
        }
      }
      
    }
  });

  
  return finalMatches;
}

/**
 * Find perfect matches for auto-assignment
 * A perfect match requires:
 * 1. Teacher self-nominated for the subject
 * 2. Curriculum leader nominated the teacher for the subject  
 * 3. Teacher has sufficient available minutes
 * 4. Deduplicated - only one match per teacher per subject (best available slot)
 * 5. (Future: Schedule compatibility - days they're working)
 */
/**
 * Enhanced perfect matches using class code intelligence
 */
export function findIntelligentPerfectMatches(
  classes: any[], // Grid classes
  staff: any[], // All staff
  intentions: TeachingIntention[],
  curriculumSubjects: CurriculumSubject[],
  getTeacherLoad: (teacherCode: string, semester: string) => { availableMinutes: number; allocatedMinutes: number },
  lines?: any[] // Grid lines with actual line IDs
): Array<{
  classId: string;
  className: string;
  lineId: string;
  teacherCode: string;
  teacherName: string;
  subjectName: string;
  reasons: string[];
  availableMinutes: number;
  requiredMinutes: number;
  matchType: 'intelligent' | 'perfect';
  confidence: number;
  classCodeAnalysis?: ClassCodeComponents;
}> {
  
  // Get intelligent matches using class code analysis
  const intelligentMatches = findIntelligentMatches(classes, staff, intentions, curriculumSubjects, lines);
  
  const enhancedMatches = [];
  
  for (const match of intelligentMatches) {
    const classItem = classes.find(c => c.id === match.classId);
    const teacher = staff.find(s => s.initial === match.teacherCode);
    
    if (!classItem || !teacher) continue;
    
    // Get teacher workload information
    const teacherLoad = getTeacherLoad(match.teacherCode, 'semester1');
    
    // Skip if teacher doesn't have capacity
    if (teacherLoad.availableMinutes < 440) {
      continue;
    }
    
    // Parse class code for additional context
    const classCodeAnalysis = classItem.subjectCode ? parseClassCode(classItem.subjectCode) : undefined;
    
    enhancedMatches.push({
      classId: classItem.id,
      className: classItem.className,
      lineId: match.lineId,
      teacherCode: match.teacherCode,
      teacherName: teacher.firstName + ' ' + teacher.lastName,
      subjectName: classItem.className,
      reasons: [
        ...match.reasons,
        `Confidence: ${Math.round(match.confidence * 100)}%`,
        `Match type: ${match.matchType}`
      ],
      availableMinutes: teacherLoad.availableMinutes,
      requiredMinutes: 440,
      matchType: match.confidence > 0.8 ? 'perfect' as const : 'intelligent' as const,
      confidence: match.confidence,
      classCodeAnalysis
    });
  }
  
  return enhancedMatches.sort((a, b) => b.confidence - a.confidence);
}

export function findPerfectMatches(
  classes: any[], // Grid classes
  staff: any[], // All staff
  intentions: TeachingIntention[],
  curriculumSubjects: CurriculumSubject[],
  getTeacherLoad: (teacherCode: string, semester: string) => { availableMinutes: number; allocatedMinutes: number },
  lines?: any[], // Grid lines with actual line IDs
  /** From buildPriorTeachingIndex — enables the "taught it last year" tiers. */
  priorTeaching?: Set<string>
): Array<{
  classId: string;
  className: string;
  lineId: string;
  teacherCode: string;
  teacherName: string;
  subjectName: string;
  reasons: string[];
  availableMinutes: number;
  requiredMinutes: number; // Assuming 440 minutes per class
  matchType: 'perfect';
}> {
  const allPotentialMatches: Array<any> = [];

  // Iterate through all classes and lines to find unassigned slots
  classes.forEach(classItem => {
    // Use actual grid line IDs instead of hardcoded letters
    const possibleLines = candidateLinesFor(classItem, lines);
    
    possibleLines.forEach(lineId => {
      // Skip if already assigned
      if (classItem.lineAssignments && classItem.lineAssignments[lineId]) {
        return;
      }

      const subjectName = classItem.subject || classItem.className;
      
      // Calculate preference matches for all staff for this subject
      assignableStaff(staff).forEach(teacher => {
        const match = calculatePreferenceStrength(
          teacher.initial,
          subjectName,
          intentions,
          curriculumSubjects,
          classItem.curriculumArea
        );

        // Check if this is a perfect match (strong preference)
        if (match.strength === 'strong' && match.selfNominated && match.curriculumNominated) {
          if (teacherAlreadyOnLine(classes, lineId, teacher.initial, classItem.id)) {
            return;
          }

          const semester = semesterForLine(lineId, lines);
          const load = getTeacherLoad(teacher.initial, semester);

          const requiredMinutes = classItem.minutesPerWeek || 440;

          // TODO: Add schedule compatibility check (days working vs line days)
          if (load.availableMinutes - load.allocatedMinutes >= requiredMinutes) {
            allPotentialMatches.push({
              classId: classItem.id,
              className: classItem.className,
              lineId,
              teacherCode: teacher.initial,
              teacherName: teacher.name,
              subjectName,
              reasons: match.reasons,
              availableMinutes: load.availableMinutes,
              requiredMinutes,
              matchType: 'perfect',
              priorityTier: assignmentPriorityTier(
                match,
                taughtLastYear(priorTeaching, teacher.initial, classItem.className)
              ),
              taughtLastYear: taughtLastYear(priorTeaching, teacher.initial, classItem.className),
              matchScore: match.matchScore,
              curriculumPreferenceLevel: match.curriculumPreferenceLevel,
              preferenceRank: match.selfPreferenceRank
            });
          }
        }
      });
    });
  });

  // Deduplicate: Keep only the best match for each teacher-subject combination
  const deduplicatedMatches = deduplicateMatches(allPotentialMatches);
  return sortByPriority(enforceTeacherCapacity(deduplicatedMatches, getTeacherLoad, lines));
}

/**
 * Get preference strength indicator colors/styles
 */
export function getPreferenceIndicator(strength: PreferenceStrength) {
  switch (strength) {
    case 'strong':
      return {
        color: 'text-ok-fg',
        bg: 'bg-ok-bg',
        border: 'border-ok-border',
        icon: '🟢',
        label: 'Strong Match'
      };
    case 'medium':
      return {
        color: 'text-warn-fg',
        bg: 'bg-warn-bg',
        border: 'border-warn-border',
        icon: '🟡',
        label: 'Good Match'
      };
    case 'weak':
      return {
        color: 'text-ink-700',
        bg: 'bg-ink-100',
        border: 'border-ink-300',
        icon: '⚪',
        label: 'Available'
      };
    case 'avoid':
      return {
        color: 'text-danger-fg',
        bg: 'bg-danger-bg',
        border: 'border-danger-border',
        icon: '🔴',
        label: 'Not Recommended'
      };
  }
}

// ============================================
// HR-Approved Intention Filtering
// ============================================

/**
 * Filter intentions to only include HR-approved ones
 * These are the intentions that flow into the grid preference system
 */
export function filterHRApprovedIntentions(intentions: TeachingIntention[]): TeachingIntention[] {
  return intentions.filter(i => i.status === 'hr_approved');
}

/**
 * Calculate curriculum area ranking multiplier for scoring
 * Rank 1: 110% multiplier (top preference)
 * Rank 2: 100% multiplier
 * Rank 3-6: Decreasing multipliers
 */
export function getCurriculumAreaRankMultiplier(rank: number): number {
  const multipliers: Record<number, number> = {
    1: 1.10,  // 110% for top preference
    2: 1.00,  // 100% for second
    3: 0.90,  // 90% for third
    4: 0.80,
    5: 0.70,
    6: 0.60
  };
  return multipliers[rank] || 0.50;
}

/**
 * Calculate enhanced self-nomination score using HR-approved intention data
 * Priority order: 1) Specific subjects, 2) Curriculum areas (ranked), 3) Year levels
 */
export function calculateEnhancedSelfNominationScore(
  teacherCode: string,
  subjectName: string,
  hrApprovedIntentions: TeachingIntention[]
): { score: number; details: { rank?: number; matchType: string; reason: string } } {
  // Find teacher's HR-approved intention
  const intention = hrApprovedIntentions.find(i => i.staffId === teacherCode);

  if (!intention) {
    return {
      score: 0,
      details: { matchType: 'none', reason: 'No HR-approved intention found' }
    };
  }

  // Check if intention has the new preferences structure
  const preferences = intention.preferences as PreferencesData | undefined;
  const subjectLower = subjectName.toLowerCase();

  // Extract core subject name for matching (remove year level suffixes like "S1", "S2")
  const coreSubjectName = subjectLower.replace(/\s*(s1|s2)$/i, '').trim();

  // PRIORITY 1: Check specific subjects FIRST (highest priority - most precise)
  if (preferences?.subjects && preferences.subjects.length > 0) {
    for (const subject of preferences.subjects) {
      const prefSubjectLower = subject.subjectName.toLowerCase();

      // Exact match or significant substring match
      if (coreSubjectName === prefSubjectLower ||
          coreSubjectName.includes(prefSubjectLower) ||
          prefSubjectLower.includes(coreSubjectName) ||
          // Handle cases like "ACCOUNTING12" matching "Accounting"
          coreSubjectName.replace(/\d+/g, '').trim() === prefSubjectLower.replace(/\d+/g, '').trim()) {
        return {
          score: 50, // Highest score - explicit subject preference
          details: {
            matchType: 'specific-subject',
            reason: `Explicitly selected "${subject.subjectName}"`
          }
        };
      }
    }
  }

  // PRIORITY 2: Check curriculum areas (ranked by preference)
  if (preferences?.curriculumAreas && preferences.curriculumAreas.length > 0) {
    for (const area of preferences.curriculumAreas) {
      const areaLower = area.areaName.toLowerCase();

      // Check if subject matches this curriculum area
      if (coreSubjectName.includes(areaLower) ||
          areaLower.includes(coreSubjectName.replace(/\d+/g, '').trim()) ||
          // Check if the core subject word matches the area
          coreSubjectName.split(/\d+/)[0].trim() === areaLower) {
        const multiplier = getCurriculumAreaRankMultiplier(area.rank);
        const baseScore = 45; // Base score for matching area
        const score = Math.round(baseScore * multiplier);

        return {
          score: Math.min(50, score),
          details: {
            rank: area.rank,
            matchType: 'curriculum-area-ranked',
            reason: `${area.rank}${getOrdinalSuffix(area.rank)} ranked curriculum area "${area.areaName}"`
          }
        };
      }
    }
  }

  // PRIORITY 3: Check year level match (lowest priority)
  if (preferences?.yearLevels && preferences.yearLevels.length > 0) {
    // Extract year level from subject name (e.g., "ENGLISH12" -> "year12")
    const yearMatch = subjectName.match(/(\d+)/);
    if (yearMatch) {
      const yearLevel = `year${yearMatch[1]}`;
      if (preferences.yearLevels.includes(yearLevel)) {
        return {
          score: 20,
          details: {
            matchType: 'year-level',
            reason: `Year ${yearMatch[1]} in preferred year levels`
          }
        };
      }
    }
  }

  // Fall back to legacy teaching preferences structure
  return calculateTeachingIntentionsScore(teacherCode, subjectName, hrApprovedIntentions);
}

/**
 * Calculate preference strength using only HR-approved intentions
 * This is the production-ready version for the grid preference system
 */
export function calculateHRApprovedPreferenceStrength(
  teacherCode: string,
  subjectName: string,
  allIntentions: TeachingIntention[],
  curriculumSubjects: CurriculumSubject[]
): TeacherPreferenceMatch {
  // Filter to HR-approved intentions only
  const hrApprovedIntentions = filterHRApprovedIntentions(allIntentions);

  // Use enhanced scoring for HR-approved intentions
  const selfNominationResult = calculateEnhancedSelfNominationScore(
    teacherCode,
    subjectName,
    hrApprovedIntentions
  );

  // Curriculum preferences score (unchanged)
  const curriculumResult = calculateCurriculumScore(teacherCode, subjectName, curriculumSubjects);

  // Handle avoid preference
  if (curriculumResult.score === -100) {
    const teacherName = hrApprovedIntentions.find(i => i.staffId === teacherCode)?.staffName || teacherCode;
    return {
      teacherCode,
      teacherName,
      strength: 'avoid',
      reasons: [curriculumResult.details.reason],
      selfNominated: selfNominationResult.score > 0,
      curriculumNominated: true,
      curriculumPreferenceLevel: 'avoid',
      selfPreferenceRank: selfNominationResult.details.rank,
      matchScore: 0
    };
  }

  // Calculate total score
  const totalScore = selfNominationResult.score + curriculumResult.score;

  // Determine strength
  let strength: PreferenceStrength;
  if (totalScore >= 70) {
    strength = 'strong';
  } else if (totalScore >= 40) {
    strength = 'medium';
  } else if (totalScore >= 25) {
    strength = 'weak';
  } else {
    strength = 'weak';
  }

  // Build reasons
  const reasons: string[] = [];
  if (selfNominationResult.score >= 20) {
    reasons.push(selfNominationResult.details.reason);
  }
  if (curriculumResult.score > 0) {
    reasons.push(curriculumResult.details.reason);
  }
  if (reasons.length === 0) {
    reasons.push('No specific preference indicated');
  }

  // Get teacher name
  const intention = hrApprovedIntentions.find(i => i.staffId === teacherCode);
  const teacherName = intention?.staffName || teacherCode;

  return {
    teacherCode,
    teacherName,
    strength,
    reasons,
    selfNominated: selfNominationResult.score > 0,
    curriculumNominated: curriculumResult.score > 0,
    curriculumPreferenceLevel: curriculumResult.details.level,
    selfPreferenceRank: selfNominationResult.details.rank,
    matchScore: totalScore
  };
}